import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  downloadMemorialDescritivo,
  fetchMemorialDownloadStatus,
  isMemorialDescritivoInstance,
  MemorialDownloadStatus,
} from '../../utils/memorialDescritivo';
import { DocumentInstance } from '../../types';
import { useModal } from '../../context/ModalContext';

interface MemorialDownloadButtonsProps {
  instance: DocumentInstance;
  className?: string;
}

const MemorialDownloadButtons: React.FC<MemorialDownloadButtonsProps> = ({
  instance,
  className = '',
}) => {
  const { showAlert } = useModal();
  const [status, setStatus] = useState<MemorialDownloadStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [downloading, setDownloading] = useState<'docx' | 'pdf' | null>(null);
  const [showFormatModal, setShowFormatModal] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!isMemorialDescritivoInstance(instance)) {
      setStatus(null);
      return;
    }
    setLoadingStatus(true);
    try {
      const data = await fetchMemorialDownloadStatus(instance.id);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, [instance]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, instance.status, instance.updated_at]);

  if (!isMemorialDescritivoInstance(instance)) {
    return null;
  }

  const handleDownload = async (format: 'docx' | 'pdf') => {
    try {
      setDownloading(format);
      await downloadMemorialDescritivo(instance.id, format);
    } catch (error: unknown) {
      const err = error as { response?: { data?: Blob | { detail?: string } } };
      let message = 'Não foi possível gerar o download do Memorial Descritivo.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text) as { detail?: string };
          if (json.detail) message = json.detail;
        } catch {
          /* ignore */
        }
      } else if (data && typeof data === 'object' && 'detail' in data && data.detail) {
        message = String(data.detail);
      }
      showAlert({ title: 'Erro no download', message, variant: 'danger' });
    } finally {
      setDownloading(null);
    }
  };

  const canDownload = status?.can_download === true;
  const unavailableReason =
    status && status.missing_fields.length > 0
      ? `Pendentes: ${status.missing_fields.join(', ')}`
      : 'Finalize o documento e preencha todos os campos obrigatórios.';

  return (
    <>
      <div className={`d-flex flex-wrap gap-2 align-items-center ${className}`}>
        {loadingStatus && <Spinner animation="border" size="sm" />}
        <Button
          variant="outline-primary"
          size="sm"
          disabled={loadingStatus || downloading !== null}
          onClick={() => setShowFormatModal(true)}
        >
          {downloading ? 'Gerando...' : 'Baixar MD'}
        </Button>
      </div>

      <Modal show={showFormatModal} onHide={() => setShowFormatModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download do Memorial Descritivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Escolha o formato do arquivo:</p>
          {!canDownload && status && (
            <div className="text-warning small mb-3">{unavailableReason}</div>
          )}
          <div className="d-grid gap-2">
            <Button
              variant="outline-primary"
              disabled={!canDownload || downloading !== null}
              onClick={async () => {
                await handleDownload('docx');
                setShowFormatModal(false);
              }}
            >
              {downloading === 'docx' ? 'Gerando Word...' : 'Word (.docx)'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={!canDownload || downloading !== null}
              onClick={async () => {
                await handleDownload('pdf');
                setShowFormatModal(false);
              }}
            >
              {downloading === 'pdf' ? 'Gerando PDF...' : 'PDF'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};


export default MemorialDownloadButtons;
